import {Injectable} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Branch } from '@core/models/branch.model';
import { BranchService } from '@core/services/branch.service';
import { Observable, Subject } from "rxjs";
import {Pageable} from "@core/pagination/pageable";
import {Message} from "@modules/communication/models/message";
import {environment} from "@env";
import { filter, map, switchMap, take } from "rxjs/operators";
import {HydraCollection} from "@core/api/hydra/hydra-collection";
import {PageableFactory} from "@core/pagination/pageable.factory";

@Injectable({providedIn: 'root'})

export class CommunicationService {
  constructor(
    private httpClient: HttpClient,
    private branchService: BranchService
  ) {
  }

  private toggleSubject = new Subject<boolean>();
  toggle$ = this.toggleSubject.asObservable();

  private markedAsReadSubject = new Subject<string>();
  markedAsRead$ = this.markedAsReadSubject.asObservable();

  private messagesToShowSubject = new Subject<Message[]>();
  messagesToShow$ = this.messagesToShowSubject.asObservable();

  private unreadCountSubject = new Subject<number>();
  unreadCount$ = this.unreadCountSubject.asObservable();

  toggleMessageBox(){
    this.toggleSubject.next(true);
  }

  getMessage(id: string): Observable<Message> {
    return this.httpClient.get<Message>(environment.apiUrl + '/emails/' + id);
  }

  getMessages(criteria: any, page: number): Observable<Pageable<Message>> {
    return this.branchService.getBranch().pipe(
      filter((b: any) => b !== null),
      take(1),
      switchMap((branch: Branch) => {
        return this.httpClient.get(environment.apiUrl + '/emails?order[createdAt]=desc' + (this.branchService.vanityMode ? '&branch=' + branch.id : '')).pipe(
          map(response => new HydraCollection(response)),
          map(collection => PageableFactory.createFromHydraCollection(collection))
        );
      })
    ) as Observable<Pageable<Message>>;
  }

  getUnreadMessages() {
    this.branchService.getBranch().pipe(
      filter((b: any) => b !== null),
      take(1),
      switchMap((branch: Branch) => {
        return this.httpClient.get(environment.apiUrl + '/emails?read=false&messageType[]=mandatory&messageType[]=precarious' + (this.branchService.vanityMode ? '&branch=' + branch.id : '')).pipe(
          map(response => new HydraCollection<Message>(response)),
          map(collection => collection.members)
        );
      })
    ).subscribe(messages => this.showMessages(messages));
  }

  showMessages(messages:Message[]){
    this.messagesToShowSubject.next(messages);
  }

  markAsRead(message: Message, secondsRead: number, answers: Record<string, string[]>|null = null): Observable<boolean> {
    const answerArray: any[] = [];
    if (answers) {
      for (const questionId in answers) {
        answerArray.push({
          question: questionId,
          answer: answers[questionId]
        });
      }
    }

    return this.httpClient.post<{answersCorrect: boolean}>(environment.apiUrl + '/email_read_logs', {
      email: message['@id'],
      secondsRead,
      answers: answerArray
    }).pipe(
      map(response => {if(response.answersCorrect){ this.markedAsReadSubject.next(message['@id'].split('/').pop()??'')}; return response.answersCorrect})
    );
  }

  setUnreadCount(count: number){
    this.unreadCountSubject.next(count);
  }
}
